import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { gbp } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, ChevronRight, Search, Plus, Pencil, Trash2, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import DriverFormDialog from "@/components/DriverFormDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

const statusColor = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  inactive: "bg-slate-100 text-brand-text-muted border-brand-border",
  suspended: "bg-red-100 text-red-700 border-red-200",
  on_holiday: "bg-amber-100 text-amber-700 border-amber-200",
  off: "bg-slate-100 text-brand-text-muted border-brand-border",
  pending_documents: "bg-blue-100 text-blue-700 border-blue-200",
};

const payTypeLabel = {
  fixed: "Fixed daily",
  dpd_based: "DPD based",
  fixed_plus_fees: "Fixed + fees",
};

const emptyDriver = {
  driver_code: "", full_name: "", email: "", phone: "",
  ni_number: "", utr_number: "", start_date: "",
  status: "active", depot: "", assigned_vehicle: "",
  pay_type: "fixed", daily_rate: "", vehicle_fee: "", fuel_fee: "",
  management_fee: "", insurance_fee: "", notes: "",
  bank_name: "", account_name: "", sort_code: "", account_number: "",
};
// eslint-disable-next-line no-unused-vars
const _unused_emptyDriver = emptyDriver;

const Drivers = () => {
  const { user } = useAuth();
  const { data, isLoading, error } = useSWR("/drivers", fetcher);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const canCreate = user?.role === "platform_owner" || user?.role === "company_admin";

  const doDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/drivers/${deleting.id}?hard=true`);
      toast.success(`Driver ${deleting.full_name} permanently deleted`);
      setDeleting(null);
      mutate("/drivers");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const doReactivate = async (driver) => {
    try {
      await api.post(`/drivers/${driver.id}/reactivate`);
      toast.success(`${driver.full_name} reactivated`);
      mutate("/drivers");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reactivate failed");
    }
  };

  const filtered = React.useMemo(() => {
    if (!data) return [];
    const s = q.trim().toLowerCase();
    if (!s) return data;
    return data.filter((d) =>
      d.full_name.toLowerCase().includes(s) ||
      d.driver_code.toLowerCase().includes(s) ||
      (d.email || "").toLowerCase().includes(s));
  }, [data, q]);

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Team</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-brand-text mt-1">Drivers</h1>
          <p className="text-sm text-brand-text-muted mt-1">{data?.length || 0} subcontractor{(data?.length || 0) === 1 ? "" : "s"} across your fleet.</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative w-full md:w-72">
            <Search className="w-4 h-4 text-brand-text-muted absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="drivers-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, ID, email"
              className="pl-9 h-10"
            />
          </div>
          {canCreate && (
            <>
              <Button
                onClick={() => setOpen(true)}
                className="bg-brand-red hover:bg-brand-red-hover shrink-0"
                data-testid="driver-add-btn"
              >
                <Plus className="w-4 h-4 mr-1.5" /> New driver
              </Button>
              <DriverFormDialog
                open={open}
                onOpenChange={setOpen}
                mode="create"
                onSaved={() => mutate("/drivers")}
              />
            </>
          )}
        </div>
      </div>

      {isLoading && <div className="text-sm text-brand-text-muted">Loading drivers…</div>}
      {error && <div className="text-sm text-red-600">Failed to load drivers</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="drivers-grid">
        {filtered.map((d) => (
          <Card
            key={d.id}
            data-testid={`driver-card-${d.driver_code}`}
            className="p-5 rounded-2xl border-brand-border hover:border-brand-navy hover:shadow-brand-md hover:-translate-y-0.5 transition-[transform,box-shadow,border-color] duration-200 group"
          >
              <div className="flex items-start justify-between">
                <Link to={`/admin/drivers/${d.id}`} className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-xl bg-brand-navy text-white grid place-items-center font-heading font-bold tracking-tight">
                    {d.full_name.split(" ").map((x) => x[0]).slice(0, 2).join("")}
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-brand-text tracking-tight leading-tight truncate">{d.full_name}</div>
                    <div className="text-[11px] font-mono tracking-wider text-brand-text-muted">{d.driver_code}</div>
                  </div>
                </Link>
                <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${statusColor[d.status] || ""}`}>
                  {(d.status || "").replace("_", " ")}
                </Badge>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-brand-text-muted">Pay type</div>
                  <div className="mt-0.5 text-brand-text">{payTypeLabel[d.pay_type] || d.pay_type}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-brand-text-muted">Daily rate</div>
                  <div className="mt-0.5 font-heading font-bold text-brand-text">
                    {d.pay_type === "dpd_based" ? "—" : gbp(d.daily_rate)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-brand-text-muted">Depot</div>
                  <div className="mt-0.5 text-brand-text">{d.depot || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-brand-text-muted">Vehicle</div>
                  <div className="mt-0.5 text-brand-text font-mono">{d.assigned_vehicle || "—"}</div>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-brand-border flex items-center justify-between text-xs">
                <Link to={`/admin/drivers/${d.id}`} className="text-brand-text-muted hover:text-brand-red transition-colors inline-flex items-center gap-1">
                  Open profile <ChevronRight className="w-3.5 h-3.5" />
                </Link>
                {canCreate && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(d)}
                            data-testid={`driver-edit-${d.driver_code}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {d.status === "inactive" && (
                      <Button size="sm" variant="ghost" onClick={() => doReactivate(d)}
                              data-testid={`driver-reactivate-${d.driver_code}`}
                              title="Reactivate">
                        <RotateCcw className="w-3.5 h-3.5 text-emerald-600" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setDeleting(d)}
                            data-testid={`driver-del-${d.driver_code}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-600" />
                    </Button>
                  </div>
                )}
              </div>
          </Card>
        ))}
        {!isLoading && filtered.length === 0 && (
          <div className="col-span-full text-center py-12 text-brand-text-muted text-sm">
            <Users className="w-6 h-6 mx-auto mb-2 opacity-40" />
            No drivers match your search.
          </div>
        )}
      </div>

      <DriverFormDialog
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        mode="edit"
        driver={editing}
        onSaved={() => { setEditing(null); mutate("/drivers"); }}
      />

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="driver-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete {deleting?.full_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the driver <b>and</b> cascade-delete all of their work rows, invoices, deductions, holidays and documents. Any linked user account will also be removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="driver-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700"
                                data-testid="driver-delete-confirm">Permanently delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Drivers;
